import { Trans } from "@lingui/macro";
import { useCallback, useRef, useState } from "react";
import {
  useCurrentRefinements,
  useRefinementList,
} from "react-instantsearch-core-v7";
import { DropdownOutlined } from "src/atoms/DropDown";
import {
  FilterContentWrapper,
  ResultsButton,
  ScrollWrapper,
} from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import { ColorFilterCheckboxOzon } from "../ColorFilterCheckbox";
import { Box, Flex } from "@otrium/core";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { Checkbox } from "src/atoms/Checkbox";
import {
  sortByCategoryName,
  sortByCount,
} from "src/utils/product-listing-helpers";
import { InputTextSearch } from "../InputTextSearch";
import { debounce } from "src/utils";
import { GoodOnYouLabel } from "./Filter.styled";
import { BRAND_FACET, CONSCIOUS_FACET } from "src/utils/algolia";
import { Radio } from "src/atoms/Radio";
import { FilterType } from "src/organisms/MobileFilters";
import { useSegment } from "src/hooks/useSegment";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";

const multicolorIconLink =
  "https://colors-icons.s3.eu-central-1.amazonaws.com/multicoloured.png";

type FilterProps =
  | (
      | {
          attribute: string;
          placeholder: string;
          type: FilterType;
          totalResults: number;
          isMultiColumn?: boolean;
          limit?: number;
          handleScrollToTop: () => void;
        }
      | {
          attribute: string;
          placeholder: string;
          type: "range";
          totalResults: number;
          isMultiColumn?: boolean;
          limit?: number;
          handleScrollToTop: () => void;
        }
    ) &
      (
        | {
            showSearch: true;
            searchPlaceholder: string;
          }
        | { showSearch?: false; searchPlaceholder?: string }
      );

function Filter({
  attribute,
  placeholder,
  type,
  totalResults: total,
  showSearch,
  searchPlaceholder,
  isMultiColumn,
  limit = 1000,
  handleScrollToTop,
}: FilterProps): JSX.Element | null {
  const filterRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const { items, refine, searchForItems, canRefine } = useRefinementList({
    attribute,
    sortBy: attribute === BRAND_FACET ? sortByCount : sortByCategoryName,
    limit,
  });
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: [attribute],
  });
  const { segmentProductListFilterOpened } = useSegment();

  const toggleOpen = debounce(() => {
    setOpen((prevState) => !prevState);

    if (!isOpen) {
      void segmentProductListFilterOpened(attribute);
    }
  }, 250);

  const { handleSearchParams } = useHandleSearchParams();

  const handleSingleSelect = useCallback(
    (value: string) => {
      const selectedItem = items.filter((item) => item.isRefined);

      handleSearchParams({
        facet: attribute,
        value,
        isSingle: true,
        deletePage: true,
      });

      if (selectedItem.length) {
        refine(selectedItem[0].value);
      }

      refine(value);

      handleScrollToTop();
    },
    [items, attribute, refine, handleSearchParams, handleScrollToTop]
  );

  const handleRefine = useCallback(
    (value: string) => {
      handleSearchParams({ facet: attribute, value, deletePage: true });

      refine(value);

      handleScrollToTop();
    },
    [attribute, refine, handleSearchParams, handleScrollToTop]
  );

  useOnClickOutside(filterRef, toggleOpen, isOpen);

  return (
    <DropdownOutlined opened={isOpen} disabled={!canRefine}>
      <DropdownOutlined.Button onClick={canRefine ? toggleOpen : undefined}>
        <span>{placeholder}</span>
      </DropdownOutlined.Button>
      {isOpen ? (
        <DropdownOutlined.Items>
          <Flex
            ref={filterRef}
            paddingBlockStart={3}
            paddingBlockEnd={3}
            flexDirection="column"
            maxHeight="inherit"
          >
            {showSearch ? (
              <Box paddingBlockEnd={3}>
                <InputTextSearch
                  placeholder={searchPlaceholder}
                  handleChange={(value) =>
                    searchForItems(value.currentTarget.value)
                  }
                  onClearSearch={() => searchForItems("")}
                />
              </Box>
            ) : null}
            <ScrollWrapper>
              <FilterContentWrapper
                as={type === "color" ? "div" : "ul"}
                isMultiColumn={isMultiColumn && items.length > 1}
                count={Math.round(items.length / 2)}
              >
                {items.map((item) => {
                  const splitLabel = item.label.split("||");
                  const id = splitLabel[0];
                  const label = splitLabel[1];

                  if (type === "color") {
                    const color = splitLabel[2];

                    return (
                      <ColorFilterCheckboxOzon
                        key={id}
                        id={id}
                        checked={item.isRefined}
                        onChange={() => handleRefine(item.value)}
                        icon={color ? undefined : multicolorIconLink}
                        name={label}
                        hex={color}
                      />
                    );
                  }

                  if (type === "select") {
                    return (
                      <Box as="li" key={`${attribute}-${id}`}>
                        <Radio
                          id={id}
                          name={label}
                          checked={item.isRefined}
                          onChange={() => handleSingleSelect(item.value)}
                        />
                      </Box>
                    );
                  }

                  return (
                    <Box as="li" key={`${attribute}-${id}`}>
                      <Checkbox
                        checked={item.isRefined}
                        onChange={() => handleRefine(item.value)}
                      >
                        {label}
                      </Checkbox>
                    </Box>
                  );
                })}
              </FilterContentWrapper>
            </ScrollWrapper>

            {attribute === CONSCIOUS_FACET ? (
              <GoodOnYouLabel>
                <Trans>Provided by Good On You</Trans>®.{" "}
                <a href={`http://howwerateourbrands.otrium.com/`}>
                  <Trans>Learn how this is rated</Trans>
                </a>
              </GoodOnYouLabel>
            ) : null}

            <Flex variant="centered">
              <ResultsButton
                fullWidth
                variant="primary"
                onClick={toggleOpen}
                disabled={currentRefinements.length === 0 || total === 0}
              >
                <span>
                  {currentRefinements.length > 0 && total > 0 ? (
                    <Trans>Show {total} results</Trans>
                  ) : (
                    <Trans>Show results</Trans>
                  )}
                </span>
              </ResultsButton>
            </Flex>
          </Flex>
        </DropdownOutlined.Items>
      ) : null}
    </DropdownOutlined>
  );
}

export { Filter };
